import { SHOW_TARGET_MIN_ZOOM_EXCEPT_DRONE_AIRPLANE } from '@utils/map/mapZoom.constants';
import { LineLayerSpecification, SymbolLayerSpecification } from 'mapbox-gl';

import { FeatureTypeEnum, LayerNameEnum, SourceNameEnum } from '@/types/map.types';
import { IdentificationEnum } from '@/types/sensor/identification.types';

export const targetLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.TARGETS,
  type: 'symbol',
  source: SourceNameEnum.TARGETS,
  filter: [
    'all',
    ['==', ['get', 'featureType'], FeatureTypeEnum.TARGET_ICON],
    [
      'any',
      [
        'in',
        ['get', 'identification'],
        ['literal', [IdentificationEnum.DRONE, IdentificationEnum.VCA, IdentificationEnum.AIRCRAFT]],
      ],
      ['>=', ['zoom'], SHOW_TARGET_MIN_ZOOM_EXCEPT_DRONE_AIRPLANE],
    ],
  ],
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-size': 0.6,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-pitch-alignment': 'viewport',
    'icon-rotate': ['coalesce', ['get', 'heading'], 0],
    'icon-rotation-alignment': 'viewport',
    'symbol-z-order': 'source',
  },
  paint: {
    'icon-opacity': ['case', ['coalesce', ['feature-state', 'blinkOn'], false], 0, 1],
  },
};

export const targetBlinkingLayer: SymbolLayerSpecification = {
  ...targetLayer,
  id: LayerNameEnum.TARGETS_CONTINGENCY,
  layout: {
    ...targetLayer.layout,
    'icon-image': ['get', 'contingencyIcon'],
    'icon-rotate': 0,
  },
  paint: {
    'icon-opacity': ['case', ['coalesce', ['feature-state', 'blinkOn'], false], 1, 0],
  },
};

export const selectedTargetLayer: SymbolLayerSpecification = {
  ...targetLayer,
  id: LayerNameEnum.TARGET_SELECTION,
  source: SourceNameEnum.SELECTION,
  filter: [
    'in',
    ['get', 'featureType'],
    ['literal', [FeatureTypeEnum.TARGET_ICON, FeatureTypeEnum.EXPIRED_DRONE_ICON]],
  ],
  layout: {
    ...targetLayer.layout,
    'icon-image': ['concat', ['get', 'icon'], '-halo'],
  },
  paint: {
    'icon-opacity': ['case', ['coalesce', ['feature-state', 'blinkOn'], false], 0, 1],
  },
};

export const selectedTargetBlinkingLayer: SymbolLayerSpecification = {
  ...selectedTargetLayer,
  id: LayerNameEnum.TARGET_SELECTION_CONTINGENCY,
  layout: {
    ...selectedTargetLayer.layout,
    'icon-image': ['concat', ['get', 'contingencyIcon'], '-halo'],
    'icon-size': 0.6,
    'icon-rotate': 0,
  },
  paint: {
    'icon-opacity': ['case', ['coalesce', ['feature-state', 'blinkOn'], false], 1, 0],
  },
};

export const targetCrossLayer: LineLayerSpecification = {
  id: LayerNameEnum.TARGET_CROSSES,
  type: 'line',
  source: SourceNameEnum.TARGETS,
  filter: ['==', 'featureType', FeatureTypeEnum.TARGET_CROSS],
  layout: {
    visibility: 'visible',
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': '#FFFFFF',
    'line-width': 2,
    'line-opacity': ['get', 'opacity'],
  },
};

export const targetLinkLayer: LineLayerSpecification = {
  id: LayerNameEnum.TARGET_LINKS,
  type: 'line',
  source: SourceNameEnum.TARGETS,
  minzoom: SHOW_TARGET_MIN_ZOOM_EXCEPT_DRONE_AIRPLANE,
  filter: ['==', 'featureType', FeatureTypeEnum.TARGET_LINK],
  layout: {
    visibility: 'visible',
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': ['get', 'color'],
    'line-width': 4,
    'line-opacity': 0.5,
  },
};

export const targetFlightPlanLinksLayer: LineLayerSpecification = {
  id: LayerNameEnum.TARGET_FLIGHT_PLAN_LINKS,
  type: 'line',
  source: SourceNameEnum.TARGETS,
  minzoom: SHOW_TARGET_MIN_ZOOM_EXCEPT_DRONE_AIRPLANE,
  filter: ['==', 'featureType', FeatureTypeEnum.TARGET_FLIGHT_PLAN_LINK],
  layout: {
    visibility: 'visible',
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': ['get', 'color'],
    'line-width': 4,
    'line-opacity': 0.5,
    'line-dasharray': [2, 2],
  },
};

export const targetSpeedVector: LineLayerSpecification = {
  id: LayerNameEnum.TARGET_SPEED,
  type: 'line',
  source: SourceNameEnum.TARGETS,
  filter: [
    'all',
    ['==', ['get', 'featureType'], FeatureTypeEnum.TARGET_SPEED],
    [
      'any',
      ['in', ['get', 'identification'], ['literal', [IdentificationEnum.VCA, IdentificationEnum.AIRCRAFT]]],
      ['>=', ['zoom'], SHOW_TARGET_MIN_ZOOM_EXCEPT_DRONE_AIRPLANE],
    ],
  ],
  layout: {
    visibility: 'visible',
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': 'black',
    'line-width': ['get', 'width'],
    'line-opacity': ['get', 'opacity'],
  },
};

export const targetTrajectory: LineLayerSpecification = {
  id: LayerNameEnum.TARGET_TRAJECTORIES,
  type: 'line',
  source: SourceNameEnum.TARGETS,
  filter: [
    'all',
    ['==', ['get', 'featureType'], FeatureTypeEnum.TARGET_TRAJECTORY],
    [
      'any',
      ['in', ['get', 'identification'], ['literal', [IdentificationEnum.DRONE, IdentificationEnum.AIRCRAFT]]],
      ['>=', ['zoom'], SHOW_TARGET_MIN_ZOOM_EXCEPT_DRONE_AIRPLANE],
    ],
  ],
  layout: {
    visibility: 'visible',
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': ['get', 'color'],
    'line-width': ['get', 'width'],
    'line-opacity': ['get', 'opacity'],
  },
};

export const expiredTargetTrajectory: LineLayerSpecification = {
  ...targetTrajectory,
  id: LayerNameEnum.EXPIRED_DRONE_TRAJECTORY,
  type: 'line',
  source: SourceNameEnum.SELECTION,
};
